import { z } from 'zod';
import { IdentifierSchema, RepositoryRelativePathSchema } from './common.js';
export const WorkItemKindSchema = z.enum(['REQUIREMENT', 'DEFECT']);
export const AgentCapabilitySchema = z.enum(['database']);
const AgentRunSchema = z
    .object({
    kind: z.literal('agent'),
    prompt: RepositoryRelativePathSchema,
    skills: z.array(IdentifierSchema).max(8).optional(),
    capabilities: z.array(AgentCapabilitySchema).max(8).optional(),
    timeoutSeconds: z.number().int().min(30).max(7_200).default(1_800),
})
    .strict();
const ShellRunSchema = z
    .object({
    kind: z.literal('shell'),
    command: z.string().min(1).max(4_096),
    timeoutSeconds: z.number().int().min(1).max(7_200).default(1_800),
})
    .strict();
export const BrokerActionSchema = z.enum([
    'PUSH_BRANCH',
    'CREATE_PULL_REQUEST',
    'CLOSE_PULL_REQUEST',
    'TRIGGER_TEST_DEPLOY',
]);
const BrokerRunSchema = z
    .object({
    kind: z.literal('broker'),
    action: BrokerActionSchema,
    intentPath: RepositoryRelativePathSchema,
    timeoutSeconds: z.number().int().min(1).max(600).default(120),
})
    .strict();
export const StageRunDefinitionSchema = z.discriminatedUnion('kind', [
    AgentRunSchema,
    ShellRunSchema,
    BrokerRunSchema,
]);
export const FindingsResultSchema = z
    .object({
    kind: z.literal('findings'),
    path: RepositoryRelativePathSchema,
    onEmpty: IdentifierSchema,
    onFindings: IdentifierSchema,
})
    .strict();
export const DecisionResultSchema = z
    .object({
    kind: z.literal('decision'),
    path: RepositoryRelativePathSchema,
    onDecision: IdentifierSchema,
})
    .strict();
export const OutcomeKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)*$/u, 'must be uppercase snake case');
export const OutcomeFileSchema = z
    .object({
    outcome: OutcomeKeySchema,
})
    .strict();
export const OutcomeResultSchema = z
    .object({
    kind: z.literal('outcome'),
    path: RepositoryRelativePathSchema,
    routes: z
        .record(OutcomeKeySchema, IdentifierSchema)
        .refine((routes) => Object.keys(routes).length >= 1, 'at least one outcome route is required')
        .refine((routes) => Object.keys(routes).length <= 16, 'at most 16 outcome routes are allowed'),
})
    .strict();
export const StageResultSchema = z.discriminatedUnion('kind', [
    FindingsResultSchema,
    DecisionResultSchema,
    OutcomeResultSchema,
]);
export const DocumentArtifactSchema = z
    .object({
    kind: z.literal('document'),
    profile: z.enum(['technical-review']).optional(),
    path: RepositoryRelativePathSchema,
    name: z.string().min(1).max(200),
})
    .strict();
export const ManualRecoverySchema = z
    .object({
    action: z.literal('FIX_AND_RESUME'),
    target: IdentifierSchema,
})
    .strict();
export const StageDefinitionSchema = z
    .object({
    key: IdentifierSchema,
    name: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    /** Inject a short-lived read-only GitHub credential into this node's commands. */
    requiresGitCredentials: z.boolean().optional(),
    run: StageRunDefinitionSchema,
    result: StageResultSchema.optional(),
    artifact: DocumentArtifactSchema.optional(),
    next: IdentifierSchema.optional(),
    onFailure: IdentifierSchema.optional(),
    manualRecovery: ManualRecoverySchema.optional(),
    maxAttempts: z.number().int().min(1).max(5).default(1),
    maxVisits: z.number().int().min(1).max(10).optional(),
    onExhausted: IdentifierSchema.optional(),
})
    .strict()
    .refine((stage) => !(stage.result && stage.next), {
    message: 'a result contract owns success routing; do not also set next',
})
    .refine((stage) => (stage.maxVisits === undefined) === (stage.onExhausted === undefined), {
    message: 'maxVisits and onExhausted must be declared together',
});
export const WorkflowDefinitionSchema = z
    .object({
    apiVersion: z.literal('tcode.dev/v1alpha1'),
    kind: z.literal('Workflow'),
    metadata: z
        .object({
        key: IdentifierSchema,
        name: z.string().min(1).max(100),
        description: z.string().max(1_000).optional(),
        /**
         * When set, the Feishu assistant may continue a delivered or failed Work Item with
         * this workflow. It describes, for the intent classifier, which requests it serves.
         */
        assistantTrigger: z.string().trim().min(1).max(500).optional(),
        version: z.string().regex(/^\d+\.\d+\.\d+$/),
    })
        .strict(),
    spec: z
        .object({
        workItemKinds: z.array(WorkItemKindSchema).min(1).max(2),
        maxTransitions: z.number().int().min(1).max(100).default(32),
        stages: z.array(StageDefinitionSchema).min(1).max(48),
    })
        .strict(),
})
    .strict();
export const FindingSchema = z
    .object({
    severity: z.preprocess(normalizeFindingSeverity, z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'])),
    file: RepositoryRelativePathSchema.optional(),
    line: z.number().int().min(1).optional(),
    summary: z.string().min(1).max(500),
    recommendation: z.string().min(1).max(2_000),
})
    .strict();
export const FindingsFileSchema = z.array(FindingSchema).max(200);
export const DecisionOptionKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'must be lowercase snake_case');
export const DecisionOptionSchema = z
    .object({
    key: DecisionOptionKeySchema,
    label: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(500).optional(),
    recommended: z.boolean().default(false),
})
    .strict();
export const DecisionSupplementSchema = z
    .object({
    optionKey: DecisionOptionKeySchema,
    label: z.string().trim().min(1).max(100),
    placeholder: z.string().trim().min(1).max(200),
    attachmentHint: z.string().trim().min(1).max(300).optional(),
})
    .strict();
export const DecisionRequestSchema = z
    .object({
    // Some agent runtimes add a stable question identifier. It is metadata only;
    // routing still uses the bounded option keys below.
    id: z.string().trim().min(1).max(100).optional(),
    question: z.string().trim().min(1).max(500),
    context: z.string().trim().min(1).max(2_000).optional(),
    supplement: DecisionSupplementSchema.optional(),
    options: z.array(DecisionOptionSchema).min(2).max(4),
})
    .strict()
    .superRefine((request, context) => {
    const keys = new Set();
    for (const [index, option] of request.options.entries()) {
        if (keys.has(option.key)) {
            context.addIssue({
                code: 'custom',
                path: ['options', index, 'key'],
                message: 'decision option keys must be unique',
            });
        }
        keys.add(option.key);
    }
    if (request.options.filter((option) => option.recommended).length > 1) {
        context.addIssue({
            code: 'custom',
            path: ['options'],
            message: 'at most one decision option may be recommended',
        });
    }
    if (request.supplement && !keys.has(request.supplement.optionKey)) {
        context.addIssue({
            code: 'custom',
            path: ['supplement', 'optionKey'],
            message: 'supplement optionKey must reference an available decision option',
        });
    }
});
export const DecisionBatchSchema = z
    .object({
    questions: z.array(DecisionRequestSchema).min(1).max(10),
})
    .strict();
function normalizeFindingSeverity(value) {
    if (typeof value !== 'string')
        return value;
    const normalized = value.trim().toUpperCase();
    const aliases = {
        INFO: 'LOW',
        MINOR: 'LOW',
        MODERATE: 'MEDIUM',
        MAJOR: 'HIGH',
        BLOCKER: 'CRITICAL',
    };
    return aliases[normalized] ?? normalized;
}
//# sourceMappingURL=workflow.js.map